import { useEffect, useRef, useState, type RefObject } from 'react';
import { warmRasterFonts } from '@/shared/lib/rasterizeElement';
import { setVeilPanel } from '@/shared/ui/organisms';
import { preparePanelForVeil, type PreparedPanel } from '../lib/preparePanelForVeil';

/** 성공 화면 등장 연출(successReveal 800ms)이 끝난 직후. 넘기기(1200ms) 전에 찍기를 마친다. */
export const VEIL_PREP_DELAY_MS = 850;

/** off · 찍는 중 · 베일에 실음(화면의 모달은 내림) · 찍지 못함(화면의 모달이 따로 사라진다) */
export type VeilStage = 'off' | 'loading' | 'on' | 'failed';

interface Prep {
  promise: Promise<PreparedPanel | null>;
  done: boolean;
  result: PreparedPanel | null;
}

function startPrep(panel: HTMLElement): Prep {
  const prep: Prep = { promise: preparePanelForVeil(panel), done: false, result: null };
  void prep.promise.then((r) => {
    prep.done = true;
    prep.result = r;
  });
  return prep;
}

/**
 * 접속 승인 모달을 베일에 실어 광원장과 함께 접어 넘긴다. 찍는 동안 화면이 100ms 넘게 멈추므로
 * 성공 화면이 머무는 동안 미리 찍어 두고, 넘길 때는 자리만 맞춰 바로 싣는다. 찍기 전에 넘기기 시작하면
 * 그 자리에서 찍고, 그동안은 화면의 모달이 그대로 있다.
 */
export function useFoldPanelIntoVeil(
  panelRef: RefObject<HTMLElement | null>,
  active: boolean,
  exiting: boolean
): VeilStage {
  const [stage, setStage] = useState<VeilStage>('off');
  const prepRef = useRef<Prep | null>(null);

  useEffect(() => {
    if (!active) return;
    if (panelRef.current) warmRasterFonts(panelRef.current);
    const id = setTimeout(() => {
      if (!prepRef.current && panelRef.current) prepRef.current = startPrep(panelRef.current);
    }, VEIL_PREP_DELAY_MS);
    return () => clearTimeout(id);
  }, [active, panelRef]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!active || !exiting || !panel) return;
    let cancelled = false;
    let first = 0;
    let second = 0;
    const load = (prepared: PreparedPanel | null) => {
      if (cancelled) return;
      const canvas = prepared?.place() ?? null;
      if (!canvas) {
        setStage('failed');
        return;
      }
      setVeilPanel(canvas);
      setStage('loading');
      // 셰이더가 텍스처를 한 번 그린 다음 프레임에 화면의 모달을 내린다 — 둘 다 없는 프레임이 없게
      first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => setStage('on'));
      });
    };
    const prep = (prepRef.current ??= startPrep(panel));
    if (prep.done) load(prep.result);
    else void prep.promise.then(load);
    return () => {
      cancelled = true;
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [active, exiting, panelRef]);

  return stage;
}
